import axios from 'axios'
import { User, Task, File, Message } from '../types'

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3001/api',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authApi = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password })
    return response.data
  },

  signup: async (email: string, password: string, username?: string) => {
    const response = await api.post('/auth/signup', { email, password, username })
    return response.data
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me')
    return response.data
  },

  logout: async () => {
    const response = await api.post('/auth/logout')
    return response.data
  },
}

// Tasks API
export const tasksApi = {
  getTasks: async () => {
    const response = await api.get('/tasks')
    return response.data
  },

  createTask: async (task: Omit<Task, 'id' | 'createdAt'>) => {
    const response = await api.post('/tasks', task)
    return response.data
  },

  updateTask: async (id: string, updates: Partial<Task>) => {
    const response = await api.put(`/tasks/${id}`, updates)
    return response.data
  },

  deleteTask: async (id: string) => {
    const response = await api.delete(`/tasks/${id}`)
    return response.data
  },
}

// GPT API
export const gptApi = {
  generateSchedule: async (tasks: Task[]) => {
    const response = await api.post('/gpt/generate-schedule', { tasks })
    return response.data
  },
}

// Files API
export const filesApi = {
  uploadFiles: async (files: File[]) => {
    const formData = new FormData()
    files.forEach((file) => {
      formData.append('files', file as any)
    })
    
    const response = await api.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  getFiles: async () => {
    const response = await api.get('/files')
    return response.data
  },

  deleteFile: async (id: string) => {
    const response = await api.delete(`/files/${id}`)
    return response.data
  },
}

// Payments API
export const paymentsApi = {
  createCheckoutSession: async (planId: string) => {
    const response = await api.post('/payments/create-checkout-session', { planId })
    return response.data
  },

  getSubscription: async () => {
    const response = await api.get('/payments/subscription')
    return response.data
  },

  cancelSubscription: async () => {
    const response = await api.post('/payments/cancel-subscription')
    return response.data
  },

  // API per i pagamenti delle tranche
  createPayment: async (bookingId: string, paymentType: 'first' | 'second', codiceScontoId?: string) => {
    const response = await api.post('/payments', { bookingId, paymentType, codiceScontoId })
    return response.data
  },

  getUserPayments: async () => {
    const response = await api.get('/payments/user')
    return response.data
  },

  getUserBookings: async () => {
    const response = await api.get('/payments/user')
    return response.data.payments
  },

  getBookingDetails: async (bookingId: string) => {
    const response = await api.get(`/payments/bookings/${bookingId}`)
    return response.data
  },
}


// Bookings API
export const bookingsApi = {
  createBooking: async (bookingData: {
    tripId: string;
    numberOfQuotes: number;
    participants: Array<{ firstName: string; lastName: string; age?: number }>;
    userAddress: {
      address: string;
      city: string;
      postalCode: string;
      country: string;
      phone: string;
    };
    participationType: string;
    codiceScontoId?: string;
  }) => {
    const response = await api.post('/bookings', bookingData)
    return response.data
  },

  getUserBookings: async () => {
    const response = await api.get('/bookings')
    return response.data
  },

  getBookingDetails: async (bookingId: string) => {
    const response = await api.get(`/bookings/${bookingId}`)
    return response.data
  },

  updateBooking: async (bookingId: string, bookingData: {
    participants: Array<{ firstName: string; lastName: string }>;
  }) => {
    const response = await api.put(`/bookings/${bookingId}`, bookingData)
    return response.data
  },

  cancelBooking: async (bookingId: string) => {
    const response = await api.delete(`/bookings/${bookingId}`)
    return response.data
  },
}

// Contact API
export const contactApi = {
  sendMessage: async (message: Partial<Message> & { recaptchaToken?: string }) => {
    const response = await api.post('/contact', message)
    return response.data
  },

  getMessages: async () => {
    const response = await api.get('/contact')
    return response.data
  },

  markAsRead: async (id: string) => {
    const response = await api.put(`/contact/${id}/read`)
    return response.data
  },

  deleteMessage: async (id: string) => {
    const response = await api.delete(`/contact/${id}`)
    return response.data
  },
}

// Admin API
export const adminApi = {
  getDashboard: async (params?: { startDate?: string; endDate?: string }) => {
    const response = await api.get('/admin/dashboard', { params })
    return response.data
  },

  getUsers: async () => {
    const response = await api.get('/admin/users')
    return response.data
  },

  getUsersDetailed: async (params?: { page?: number; limit?: number; search?: string; isAdmin?: boolean }) => {
    const response = await api.get('/admin/users-detailed', { params })
    return response.data
  },

  getTripsDetailed: async (params?: { page?: number; limit?: number; isActive?: boolean }) => {
    const response = await api.get('/admin/trips-detailed', { params })
    return response.data
  },

  createTrip: async (tripData: {
    name: string
    description?: string
    deposit: number
    startDate?: string
    endDate?: string
    maxParticipants?: number
    priceSingle: number
    priceDuo: number
    priceTrio: number
    pricePocker: number
    isActive?: boolean
  }) => {
    const response = await api.post('/admin/trips', tripData)
    return response.data
  },

  updateTrip: async (id: string, tripData: {
    name?: string
    description?: string
    deposit?: number
    startDate?: string
    endDate?: string
    maxParticipants?: number
    priceSingle?: number
    priceDuo?: number
    priceTrio?: number
    pricePocker?: number
    isActive?: boolean
  }) => {
    const response = await api.put(`/admin/trips/${id}`, tripData)
    return response.data
  },

  deleteTrip: async (id: string) => {
    const response = await api.delete(`/admin/trips/${id}`)
    return response.data
  },

  getPaymentsDetailed: async (params?: { page?: number; limit?: number; status?: string; tranche?: number }) => {
    const response = await api.get('/admin/payments-detailed', { params })
    return response.data
  },

  getRefundsDetailed: async (params?: { page?: number; limit?: number; status?: string }) => {
    const response = await api.get('/admin/refunds-detailed', { params })
    return response.data
  },

  updateRefundStatus: async (id: string, data: { status: string; adminNotes?: string }) => {
    const response = await api.put(`/admin/refunds/${id}`, data)
    return response.data
  },



  // Trip Dates API
  getTripDates: async (tripId: string) => {
    const response = await api.get(`/admin/trips/${tripId}/trip-dates`)
    return response.data
  },

  createTripDate: async (tripId: string, data: { startDate: string; endDate: string }) => {
    const response = await api.post(`/admin/trips/${tripId}/trip-dates`, data)
    return response.data
  },

  // Trip Pricing API
  getTripPricing: async (tripDateId: string) => {
    const response = await api.get(`/admin/trip-dates/${tripDateId}/trip-pricing`)
    return response.data
  },

  createTripPricing: async (tripDateId: string, data: { participationType: string; occupancy: number; totalPrice: number; depositPerPerson: number; secondTrancheAmount: number }) => {
    const response = await api.post(`/admin/trip-dates/${tripDateId}/trip-pricing`, data)
    return response.data
  },

  updateTripPricing: async (id: string, data: { totalPrice?: number; depositPerPerson?: number; secondTrancheAmount?: number; isActive?: boolean }) => {
    const response = await api.put(`/admin/trip-pricing/${id}`, data)
    return response.data
  },

  deleteTripPricing: async (id: string) => {
    const response = await api.delete(`/admin/trip-pricing/${id}`)
    return response.data
  },

  promoteUser: async (id: string, isAdmin: boolean) => {
    const response = await api.put(`/admin/users/${id}/promote`, { isAdmin })
    return response.data
  },

  updateUser: async (id: string, updates: Partial<User>) => {
    const response = await api.put(`/admin/users/${id}`, updates)
    return response.data
  },

  deleteUser: async (id: string) => {
    const response = await api.delete(`/admin/users/${id}`)
    return response.data
  },

  getAnalytics: async () => {
    const response = await api.get('/admin/analytics')
    return response.data
  },

  // Products API
  getProducts: async () => {
    const response = await api.get('/admin/products')
    return response.data
  },

  createProduct: async (product: any) => {
    const response = await api.post('/admin/products', product)
    return response.data
  },

  updateProduct: async (id: string, product: any) => {
    const response = await api.put(`/admin/products/${id}`, product)
    return response.data
  },

  deleteProduct: async (id: string) => {
    const response = await api.delete(`/admin/products/${id}`)
    return response.data
  },

  // Product Orders API
  getProductOrders: async () => {
    const response = await api.get('/product-orders/admin/all')
    return response.data
  },

  updateProductOrderStatus: async (id: string, status: string) => {
    const response = await api.put(`/product-orders/admin/${id}/status`, { status })
    return response.data
  },

  // Sales Report API
  getSalesReport: async (params: { startDate: string; endDate: string; productId?: string; category?: string; customerEmail?: string }) => {
    const response = await api.get('/admin/sales-report', { params })
    return { report: response.data }
  },

  // Public shipping costs API
  getPublicShippingCosts: async () => {
    const response = await api.get('/products/shipping-costs')
    return response.data
  },

  // Shipping Costs API
  getShippingCosts: async () => {
    const response = await api.get('/admin/shipping-costs')
    return response.data
  },

  createShippingCost: async (shippingCost: any) => {
    const response = await api.post('/admin/shipping-costs', shippingCost)
    return response.data
  },

  updateShippingCost: async (id: string, shippingCost: any) => {
    const response = await api.put(`/admin/shipping-costs/${id}`, shippingCost)
    return response.data
  },

  deleteShippingCost: async (id: string) => {
    const response = await api.delete(`/admin/shipping-costs/${id}`)
    return response.data
  },

  // Bookings (admin)
  getBookingsDetailed: async (params?: { page?: number; limit?: number; status?: string; tripId?: string; userSearch?: string }) => {
    const response = await api.get('/admin/bookings-detailed', { params })
    return response.data
  },
}

// Trips API
export const tripsApi = {
  getTrips: async () => {
    const response = await api.get('/trips')
    return response.data
  },

  getTrip: async (id: string) => {
    const response = await api.get(`/trips/${id}`)
    return response.data
  },

  getTripsByName: async (name: string) => {
    const response = await api.get(`/trips/by-name/${encodeURIComponent(name)}`)
    return response.data
  },

  getPaymentInfo: async (tripId: string) => {
    const response = await api.get(`/trips/${tripId}/payment-info`)
    return response.data
  },

  resumePayment: async (paymentId: string) => {
    const response = await api.get(`/payments/${paymentId}/resume`)
    return response.data
  },

  checkAvailability: async (tripId: string) => {
    const response = await api.get(`/trips/${tripId}/availability`)
    return response.data
  },
}

// Discount Codes API
export const discountCodesApi = {
  validateCode: async (codice: string, tripId: string) => {
    const response = await api.post('/discount-codes/validate', { codice, tripId })
    return response.data
  },

  getAll: async () => {
    const response = await api.get('/discount-codes')
    return response.data
  },

  create: async (data: {
    percentualeSconto: number;
    viaggioId: string;
    nomeInfluencer: string;
    cognomeInfluencer: string;
  }) => {
    const response = await api.post('/discount-codes', data)
    return response.data
  },

  update: async (id: string, data: {
    percentualeSconto?: number;
    viaggioId?: string;
    nomeInfluencer?: string;
    cognomeInfluencer?: string;
  }) => {
    const response = await api.put(`/discount-codes/${id}`, data)
    return response.data
  },

  delete: async (id: string) => {
    const response = await api.delete(`/discount-codes/${id}`)
    return response.data
  },

  getStats: async (params?: { startDate?: string; endDate?: string }) => {
    const response = await api.get('/discount-codes/stats', { params })
    return response.data
  },
}

// Activities API
export const activitiesApi = {
  getActivities: async () => {
    const response = await api.get('/activities')
    return response.data
  },

  getActivity: async (id: string) => {
    const response = await api.get(`/activities/${id}`)
    return response.data
  },
}

// Destinations API
export const destinationsApi = {
  getDestinations: async () => {
    const response = await api.get('/destinations')
    return response.data
  },

  getDestination: async (id: string) => {
    const response = await api.get(`/destinations/${id}`)
    return response.data
  },
}

// User API
export const userApi = {
  getProfile: async () => {
    const response = await api.get('/user/profile')
    return response.data
  },

  updateProfile: async (profileData: { 
    username?: string; 
    email?: string;
    address?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    phone?: string;
  }) => {
    const response = await api.put('/user/profile', profileData)
    return response.data
  },

  changePassword: async (passwordData: { currentPassword: string; newPassword: string }) => {
    const response = await api.put('/user/change-password', passwordData)
    return response.data
  },

  deleteAccount: async () => {
    const response = await api.delete('/user/account')
    return response.data
  }
}

// Refunds API
export const refundsApi = {
  requestRefund: async (paymentId: string, reason: string) => {
    const response = await api.post('/refunds/request', { paymentId, reason });
    return response.data;
  },

  getUserRefunds: async () => {
    const response = await api.get('/refunds/user');
    return response.data;
  },
};

// Trip Configuration API (removed - using hardcoded values)
export const tripConfigApi = {
  getTripDates: async (tripId: string) => {
    const response = await api.get(`/trips/${tripId}/trip-dates`)
    return response.data
  }
}

export default api 